import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tab,
  Tabs,
  Tooltip,
} from '@material-ui/core';
import InfoIcon from '@mui/icons-material/Info';
import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomActiveInactiveRadio from '../../../../../../shared/form-controls/custom-active-inactive-radio/Custom-Active-Inactive-Radio';
import CustomExpirySelect from '../../../../../../shared/form-controls/custom-expiry-select/Custom-Expiry-Select';
import IntegrationSource from '../../../../../../shared/integration-source/IntegrationSource';
import LinearProgressBar from '../../../../../../shared/progress-bar/Progress';
import { formDataValidate } from '../../../../../../utils/formUtils';
import {
  getRoomReservation,
  updateRoomReservation,
} from '../../../../../../utils/IntegrationsAPIUtils';
import { notificationManager } from '../../../../../../utils/NotificationUtils';
import {
  fetchROomReservationConfig,
  roomReservationSelector,
  setSelectedRoomReservation,
} from '../../../../configurationSlice';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../../utils/loaderSlice';
import { ENVIRONMENT } from '../../../../../../env/Configurations';

const RoomReservation = ({ selectedHotel, showLoader = () => {} }) => {
  const roomReservationConfig = useSelector(roomReservationSelector);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    integrationSource: {
      value: 'PMS',
      fieldName: 'Integration Source',
      errors: {},
      validations: [],
    },
    integrationSourceSelected: {
      value: '',
      fieldName: 'Vendor',
      errors: {},
      validations: [],
    },
    apiIntegrationHotelId: {
      value: '',
      fieldName: 'Hotel ID',
      errors: {},
      validations: ['required'],
    },
    apiIntegrationUrl: {
      value: '',
      fieldName: 'URL',
      errors: {},
      validations: ['validUrl', 'safeDomain'],
    },
    promoCode: {
      value: '',
      fieldName: 'Promo Code',
      errors: {},
      validations: [],
    },
    payment: {
      value: '',
      fieldName: 'Payment',
      errors: {},
      validations: [],
    },
    payNow: {
      value: true,
      fieldName: 'Pay now',
      errors: {},
      validations: [],
    },
    payAtProperty: {
      value: false,
      fieldName: 'Pay at property',
      errors: {},
      validations: [],
    },
    roomUpgrades: {
      value: '',
      fieldName: 'Room Upgrades',
      errors: {},
      validations: [],
    },
    modifyBooking: {
      value: '',
      fieldName: 'Modify Booking',
      errors: {},
      validations: [],
    },
    modifyBookingExpiry: {
      value: '',
      fieldName: 'Modify Booking Expiry',
      errors: {},
      validations: [],
    },
    cancelBooking: {
      value: '',
      fieldName: 'Cancel Booking',
      errors: {},
      validations: [],
    },
    cancelBookingExpiry: {
      value: '',
      fieldName: 'Cancel Booking Expiry',
      errors: {},
      validations: [],
    },
  });

  useEffect(() => {
    if (selectedHotel) {
      fetchRoomReservations();
    }
  }, [selectedHotel]);

  const fetchRoomReservations = async () => {
    try {
      dispatch(stepUpLoaderCount());
      const res = await getRoomReservation(selectedHotel);
      dispatch(setSelectedRoomReservation(res.data.getRoomReservations));
    } catch (err) {
      dispatch(setSelectedRoomReservation(null));
    } finally {
      setLoading(false);
      dispatch(stepDownLoaderCount());
    }
  };

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const convertEnumToBoolean = (value) => {
    switch (value) {
      case 'active':
        return true;
      case 'inactive':
        return false;
      default:
        return value;
    }
  };

  const refactorPayload = () => {
    if (validate()) {
      return {
        hotelId: selectedHotel,
        payAtProperty: formData?.payAtProperty?.value,
        payNow: formData?.payNow?.value,
        payment: convertEnumToBoolean(formData?.payment?.value),
        promoCode: convertEnumToBoolean(formData?.promoCode?.value),
        roomUpgrades: convertEnumToBoolean(formData?.roomUpgrades?.value),
        modifyBooking: {
          cancelBooking: convertEnumToBoolean(formData?.modifyBooking?.value),
          expireAt: formData?.modifyBookingExpiry?.value,
        },
        cancelBooking: {
          cancelBooking: convertEnumToBoolean(formData?.cancelBooking?.value),
          expireAt: formData?.cancelBookingExpiry?.value,
        },
        integrationSource: formData?.integrationSource?.value,
        integrationSourceType: formData?.integrationSourceSelected?.value,
        apiIntegrationHotelId: formData?.apiIntegrationHotelId?.value,
        apiIntegrationURL: formData?.apiIntegrationUrl?.value,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      dispatch(stepUpLoaderCount());
      if (validate()) {
        updateRoomReservation(refactorPayload()).then((res) => {
          notificationManager('success', 'Room reservation details updated successfully');
          dispatch(fetchROomReservationConfig(selectedHotel));
        });
      }
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }

      notificationManager('danger', 'Unable to update room reservation details');
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleChange = (e, field) => {
    const currField = { ...formData[field] };
    currField.value = e.target.checked;
    setFormData({ [field]: currField });
  };

  useEffect(() => {
    const formDataCopy = { ...formData };

    if (roomReservationConfig) {
      formDataCopy.integrationSource.value = 'PMS';
      formDataCopy.integrationSourceSelected.value = roomReservationConfig?.integrationSourceType;
      formDataCopy.apiIntegrationHotelId.value = selectedHotel;
      formDataCopy.apiIntegrationUrl.value = roomReservationConfig?.apiIntegrationURL;
      formDataCopy.payAtProperty.value = roomReservationConfig?.payAtProperty;
      formDataCopy.payNow.value = roomReservationConfig?.payNow;
      formDataCopy.payment.value = roomReservationConfig?.payment ? 'active' : 'inactive';
      formDataCopy.promoCode.value = roomReservationConfig?.promoCode ? 'active' : 'inactive';
      formDataCopy.roomUpgrades.value = roomReservationConfig?.roomUpgrades ? 'active' : 'inactive';
      formDataCopy.modifyBooking.value = roomReservationConfig?.modifyBooking?.cancelBooking
        ? 'active'
        : 'inactive';
      formDataCopy.modifyBookingExpiry.value = roomReservationConfig?.modifyBooking?.expireAt;
      formDataCopy.cancelBooking.value = roomReservationConfig?.cancelBooking?.cancelBooking
        ? 'active'
        : 'inactive';
      formDataCopy.cancelBookingExpiry.value = roomReservationConfig?.cancelBooking?.expireAt;
    } else {
      formDataCopy.integrationSource.value = 'PMS';
      formDataCopy.integrationSourceSelected.value = '';
      formDataCopy.apiIntegrationHotelId.value = '';
      formDataCopy.apiIntegrationUrl.value = '';
      formDataCopy.payAtProperty.value = '';
      formDataCopy.payNow.value = true;
      formDataCopy.payment.value = 'inactive';
      formDataCopy.promoCode.value = 'inactive';
      formDataCopy.roomUpgrades.value = 'inactive';
      formDataCopy.modifyBooking.value = 'inactive';
      formDataCopy.modifyBookingExpiry.value = '';
      formDataCopy.cancelBooking.value = 'inactive';
      formDataCopy.cancelBookingExpiry.value = '';
    }
    setFormData(formDataCopy);
  }, [roomReservationConfig, selectedHotel]);

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else {
      setTabValue(newValue);
    }
  };

  return loading ? (
    <div className='pt-4 col-12'>
      <LinearProgressBar />
    </div>
  ) : (
    <form onSubmit={handleSubmit} className='room-reservation' noValidate>
      <div className='col-12 hotel-service-tab-section'>
        <div className='col col-xl-6'>
          <Tabs
            value={tabValue}
            variant='fullWidth'
            indicatorColor='primary'
            onChange={handleTabChange}
            aria-label='roomTypes-add-tabs'
            textColor='primary'
            className='property-tabs'
          >
            <Tab label='Integration Settings' />
            <Tab label='Configurable features' />
          </Tabs>
        </div>
      </div>

      {tabValue === 0 && (
        <div className='mx-4'>
          <IntegrationSource
            formData={formData}
            setFormData={setFormData}
            section='Room Reservation'
            headTitle='Integration Source'
            showLoader={showLoader}
          />
        </div>
      )}

      {tabValue === 1 && (
        <div className='mx-4'>
          <div className='hotel-service-section'>
            <div className='hotel-service-subtitle'>{formData.promoCode.fieldName}</div>
            <CustomActiveInactiveRadio
              item={formData.promoCode}
              handleRadioButton={(e) => {
                setFormData({ promoCode: e });
              }}
            />
          </div>
          <div className='hotel-service-section'>
            <div className='hotel-service-subtitle'>
              {formData.payment.fieldName}
              <Tooltip title='Please select minimum of one option' placement='right'>
                <InfoIcon fontSize='small' className='ms-3' />
              </Tooltip>
            </div>
            <CustomActiveInactiveRadio
              item={formData.payment}
              handleRadioButton={(e) => {
                setFormData({ payment: e });
              }}
            />
            {formData.payment.value === 'active' && (
              <div>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        checked={formData.payNow.value}
                        onChange={(e) => handleChange(e, 'payNow')}
                        name='pay-now'
                      />
                    }
                    label='Pay now'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        checked={formData.payAtProperty.value}
                        onChange={(e) => handleChange(e, 'payAtProperty')}
                        name='pay-at-property'
                      />
                    }
                    label='Pay at property'
                  />
                </FormGroup>
              </div>
            )}
          </div>
          <div className='hotel-service-section'>
            <div className='hotel-service-subtitle'>{formData.cancelBooking.fieldName}</div>
            <CustomActiveInactiveRadio
              item={formData.cancelBooking}
              handleRadioButton={(e) => {
                setFormData({ cancelBooking: e });
              }}
            />
            {formData.cancelBooking.value === 'active' && (
              <CustomExpirySelect
                item={formData.cancelBookingExpiry}
                onExirySelectChange={(e) => {
                  setFormData({ cancelBookingExpiry: e });
                }}
              />
            )}
          </div>
          <div className='hotel-service-section'>
            <div className='hotel-service-subtitle'>{formData.modifyBooking.fieldName}</div>
            <CustomActiveInactiveRadio
              item={formData.modifyBooking}
              handleRadioButton={(e) => {
                setFormData({ modifyBooking: e });
              }}
            />
            {formData.modifyBooking.value === 'active' && (
              <CustomExpirySelect
                item={formData.modifyBookingExpiry}
                onExirySelectChange={(e) => {
                  setFormData({ modifyBookingExpiry: e });
                }}
              />
            )}
          </div>
          <div className='hotel-service-section'>
            <div className='hotel-service-subtitle'>{formData.roomUpgrades.fieldName}</div>
            <CustomActiveInactiveRadio
              item={formData.roomUpgrades}
              handleRadioButton={(e) => {
                setFormData({ roomUpgrades: e });
              }}
            />
          </div>
          <div className='row mt-auto w-100 pb-5'>
            <div className='col-md-12 mt-3  d-flex justify-content-start'>
              <Button variant='contained' type='submit' color='primary' size='large'>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default RoomReservation;
